import React from 'react';
import { Skeleton } from '@/Components/ui/skeleton';
import { Card, CardContent } from '@/Components/ui/card';
import { Stack } from '@/Components/ui/layout';
import { Inline } from '@/Components/ui/layout';
import { Separator } from '@/Components/ui/separator';

// Skeleton for Job Header
export const JobHeaderSkeleton = () => (
  <Card>
    <CardContent className="p-4">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex-1 space-y-3">
          <Skeleton className="h-7 w-[250px]" />
          <Inline spacing="md" className="flex-wrap">
            <Skeleton className="h-9 w-[140px]" />
            <Skeleton className="h-9 w-[100px]" />
          </Inline>
          <Inline spacing="md" className="flex-wrap">
            <Skeleton className="h-5 w-[120px]" />
            <Skeleton className="h-5 w-[120px]" />
            <Skeleton className="h-5 w-[120px]" />
          </Inline>
        </div>
        <Card className="p-4 text-center">
          <Skeleton className="h-10 w-10 rounded-full mx-auto mb-2" />
          <Skeleton className="h-4 w-[80px] mx-auto" />
        </Card>
      </div>
    </CardContent>
  </Card>
);

// Skeleton for Candidate Card
export const CandidateCardSkeleton = () => (
  <Card>
    <CardContent className="p-4 space-y-3">
      {/* Compact Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          <Skeleton className="h-10 w-10 rounded-full" />
          <div className="flex items-center gap-2 flex-wrap">
            <Skeleton className="h-5 w-[120px]" />
            <Separator orientation="vertical" className="h-4" />
            <Skeleton className="h-5 w-[180px]" />
            <Separator orientation="vertical" className="h-4" />
            <div className="flex gap-2">
              <Skeleton className="h-4 w-[80px]" />
              <Skeleton className="h-4 w-[80px]" />
              <Skeleton className="h-4 w-[80px]" />
              <Skeleton className="h-4 w-[80px]" />
            </div>
          </div>
        </div>
        <Skeleton className="h-6 w-[80px] rounded-full" />
      </div>

      {/* Assessment & Details */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <Inline spacing="md" className="flex-wrap">
          <Skeleton className="h-4 w-[140px]" />
          <Skeleton className="h-4 w-[140px]" />
          <Skeleton className="h-4 w-[140px]" />
          <Skeleton className="h-4 w-[140px]" />
        </Inline>
        <Inline spacing="md" className="flex-wrap">
          <Skeleton className="h-4 w-[100px]" />
          <Skeleton className="h-4 w-[100px]" />
        </Inline>
      </div>

      {/* Action Buttons */}
      <Inline spacing="sm" className="flex-wrap">
        <Skeleton className="h-8 w-[120px]" />
        <Skeleton className="h-8 w-[120px]" />
        <Skeleton className="h-8 w-[120px]" />
        <Skeleton className="h-8 w-[120px]" />
        <Skeleton className="h-8 w-[120px]" />
        <Skeleton className="h-8 w-[120px]" />
        <Skeleton className="h-8 w-[80px]" />
      </Inline>
    </CardContent>
  </Card>
);

// Skeleton for Filters Sidebar
export const FiltersSidebarSkeleton = () => (
  <Card className="w-full sm:max-w-md">
    <CardContent className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-[180px]" />
        <Skeleton className="h-6 w-6 rounded-full" />
      </div>
      
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-4 w-[150px]" />
          <Skeleton className="h-6 w-[80px]" />
        </div>

        <Stack spacing="md">
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <div key={item} className="space-y-2">
              <Skeleton className="h-4 w-[80px]" />
              <Skeleton className="h-9 w-full" />
            </div>
          ))}
        </Stack>

        <div className="space-y-3">
          <Skeleton className="h-5 w-[120px]" />
          <div className="flex gap-2 flex-wrap">
            <Skeleton className="h-6 w-[100px] rounded-full" />
            <Skeleton className="h-6 w-[100px] rounded-full" />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Skeleton for Upload Popup
export const UploadPopupSkeleton = () => (
  <Card className="max-w-2xl">
    <CardContent className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Skeleton className="h-6 w-[200px]" />
        <Skeleton className="h-6 w-6 rounded-full" />
      </div>
      <div className="space-y-4">
        <Skeleton className="h-4 w-[300px]" />
        <Skeleton className="h-4 w-[120px]" />
        
        <div className="space-y-4">
          <Skeleton className="h-[60px] w-[200px] rounded-lg mx-auto" />
          <div className="text-center space-y-2">
            <Skeleton className="h-4 w-[250px] mx-auto" />
            <Skeleton className="h-4 w-[250px] mx-auto" />
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);

// Skeleton for Pagination Info
export const PaginationInfoSkeleton = () => (
  <div className="text-center py-4">
    <Skeleton className="h-4 w-[200px] mx-auto" />
  </div>
);

// Skeleton for No Candidates State
export const NoCandidatesSkeleton = () => (
  <Card>
    <CardContent className="p-6 text-center space-y-4">
      <Skeleton className="h-5 w-[300px] mx-auto" />
      <Skeleton className="h-8 w-[120px] mx-auto rounded-md" />
    </CardContent>
  </Card>
);

// Main Application List Skeleton
export const ApplicationListSkeleton = () => (
  <Stack spacing="md">
    <JobHeaderSkeleton />
    <Stack spacing="md">
      {[1, 2, 3, 4, 5].map((item) => (
        <CandidateCardSkeleton key={`skeleton-candidate-${item}`} />
      ))}
    </Stack>
    <PaginationInfoSkeleton />
  </Stack>
);

// Loading More Candidates Skeleton (for pagination)
export const LoadingMoreCandidatesSkeleton = () => (
  <Stack spacing="md" className="mt-5">
    {[1, 2, 3].map((item) => (
      <CandidateCardSkeleton key={`loading-more-${item}`} />
    ))}
  </Stack>
);

// Complete Applications Page Skeleton
export const ApplicationsPageSkeleton = () => (
  <Stack spacing="md">
    <JobHeaderSkeleton />
    <Stack spacing="md">
      {[1, 2, 3, 4, 5].map((item) => (
        <CandidateCardSkeleton key={`skeleton-candidate-${item}`} />
      ))}
    </Stack>
    <PaginationInfoSkeleton />
  </Stack>
);
